import { themes } from "constants/theme"

/**
 * AI Assistant personas
 *
 * The "DS AI Assistant" (pro-header chat) shows a persona that follows the account's
 * adventure mode — the same theme that already brands the whole app: Earl on Sportsman
 * (blue), Ruth on Sportswoman (pink). The theme is the single source of truth, so the
 * assistant can never disagree with the logo, favicon and primary color around it.
 *
 * Colors deliberately live in the theme, not here: `--color-primary` already resolves to
 * the brand blue/pink via the `body.theme-*` class (see src/stylesheets/variables.css), so
 * the assistant picks up the right accent for free.
 */

const ASSISTANT_IDS = Object.freeze({
  EARL: "earl",
  RUTH: "ruth"
})

// Both personas share one greeting sentence (see en.json
// `ai_assistant.personas.greeting_template`) — only the interpolated name and product name
// differ, so the template lives in a single i18n key rather than being duplicated.
const GREETING_TEMPLATE_KEY = "ai_assistant.personas.greeting_template"

const ASSISTANTS = Object.freeze({
  [ASSISTANT_IDS.EARL]: {
    id: ASSISTANT_IDS.EARL,
    nameKey: "ai_assistant.personas.earl.name",
    productNameKey: "ai_assistant.personas.earl.product_name",
    greetingKey: GREETING_TEMPLATE_KEY
  },
  [ASSISTANT_IDS.RUTH]: {
    id: ASSISTANT_IDS.RUTH,
    nameKey: "ai_assistant.personas.ruth.name",
    productNameKey: "ai_assistant.personas.ruth.product_name",
    greetingKey: GREETING_TEMPLATE_KEY
  }
})

/**
 * Resolves which persona to show for a theme.
 *
 * `preference` is checked first even though nothing sets it yet — deliberate, so a
 * user-facing override can be added later without revisiting this resolution order.
 *
 * Anything other than Sportswoman falls back to Earl, matching the theme system's own
 * default (`adventure_mode` is a non-null enum defaulting to `sportsman`, and
 * AntdThemeContext falls back to `sportsman` when nothing is set).
 */
const resolveAssistantId = ({ preference, theme } = {}) => {
  if (preference && ASSISTANTS[preference]) return preference
  return theme === themes.sportswoman ? ASSISTANT_IDS.RUTH : ASSISTANT_IDS.EARL
}

const resolveAssistant = (args) => ASSISTANTS[resolveAssistantId(args)]

export { ASSISTANTS, ASSISTANT_IDS, resolveAssistantId, resolveAssistant }
